import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Result, Skeleton } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { ChatContainer } from "../components/chat";
import { useAuth } from "../contexts/AuthContext";
import { useChat } from "../contexts/ChatContext";

const ChatPage = () => {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const {
    currentConversation,
    startConversation,
    isConnected,
    fetchConversations,
    loading: chatLoading,
  } = useChat();

  /* === Initialisation du chat === */
  useEffect(() => {
    if (authLoading || !user?.id) {
      console.log("⏳ En attente de l’authentification...", {
        authLoading,
        hasUser: !!user,
        userId: user?.id,
      });
      return;
    }

    console.log("✅ Chat init avec utilisateur:", { userId: user.id });

    const initializeChat = async () => {
      try {
        if (quoteId) {
          console.log("🎯 Démarrage conversation avec quoteId:", quoteId);
          await startConversation(quoteId);
        } else {
          console.log("📥 Récupération des conversations...");
          await fetchConversations();
        }
      } catch (error) {
        console.error("❌ Erreur d'initialisation du chat:", error);
      }
    };

    initializeChat();
  }, [quoteId, startConversation, fetchConversations, user, authLoading]);

  const handleBack = () => {
    navigate(-1);
  };

  /* === Loading / Connexion === */
  if (!isConnected) {
    return (
      <div className="h-screen flex items-center justify-center p-4 bg-[#1a1a1d]">
        <div className="text-center text-gray-400">
          <Skeleton active paragraph={{ rows: 4 }} />
          <p className="mt-4">Connexion au chat en cours...</p>
        </div>
      </div>
    );
  }

  if (chatLoading) {
    return (
      <div className="h-screen flex items-center justify-center p-4 bg-[#1a1a1d]">
        <div className="text-center text-gray-400">
          <Skeleton active paragraph={{ rows: 4 }} />
          <p className="mt-4">Chargement de la conversation...</p>
        </div>
      </div>
    );
  }

  /* === Erreur conversation introuvable === */
  if (quoteId && !currentConversation) {
    if (chatLoading) {
      return (
        <div className="h-screen flex items-center justify-center bg-[#1a1a1d]">
          <Skeleton active paragraph={{ rows: 6 }} />
        </div>
      );
    }

    return (
      <div className="h-screen flex items-center justify-center p-4 bg-[#1a1a1d]">
        <Result
          status="404"
          title={<span className="text-gray-200">Conversation non trouvée</span>}
          subTitle={
            <span className="text-gray-400">
              Désolé, nous n'avons pas pu trouver cette conversation.
            </span>
          }
          extra={
            <Button
              onClick={handleBack}
              style={{
                backgroundColor: "#d4af37",
                border: "none",
                color: "#111",
                fontWeight: "bold",
              }}
            >
              <ArrowLeftOutlined /> Retour
            </Button>
          }
        />
      </div>
    );
  }

  /* === Page principale === */
  return (
    <div className="h-screen flex flex-col bg-[#1a1a1d] text-gray-200">
      {/* Header */}
      <div className="p-4 border-b border-gray-700 bg-[#111]">
        <div className="max-w-6xl mx-auto flex items-center">
          <Button
            type="text"
            icon={<ArrowLeftOutlined style={{ color: "#d4af37" }} />}
            onClick={handleBack}
            className="mr-4"
          >
            <span className="text-gray-200">Retour</span>
          </Button>
          <h1 className="text-xl font-semibold text-gray-200">
            {quoteId ? "💬 Discuter avec le client" : "📨 Messages"}
          </h1>
        </div>
      </div>

      {/* Chat Container */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full max-w-6xl mx-auto">
          <ChatContainer quoteId={quoteId} className="h-full" />
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
